import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { Typography } from '@tkww/the-bash-frontend';
import ReviewCards from 'components/Reviews/ReviewCards';

import { selectReviews } from 'state/modules/profiles';
import Pagination from 'components/Pagination';
import { REVIEWS_PER_STANDARD_PAGE, reviewTypes } from 'constants/Profiles';
import Dropdown from 'components/Dropdown';

import api from 'api/profiles';
import { REVIEWS_SORT_TYPES, SORT_BY_OPTIONS, SORT_LABEL_BY_VALUE } from 'constants/reviews';
import { ctaClickEvent } from 'state/modules/analytics';

const ReviewsSection = ({ profile, originalQuery, type }) => {
  const dispatch = useDispatch();
  const { profileName, profileUrl, numberOfReviews: totalReviews } = profile;
  const { page, widget, skip, take } = originalQuery;
  const initialReviews = useSelector((state) => selectReviews(state, profileUrl, page, widget));
  const [reviews, setReviews] = React.useState(initialReviews);
  const [sortType, setSortType] = React.useState(REVIEWS_SORT_TYPES.RECOMMENDED);

  const retrieveReviews = async () => {
    try {
      const response = await api.getReviews(profileUrl, skip, take, sortType);
      setReviews(response);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    if (parseInt(sortType) !== REVIEWS_SORT_TYPES.RECOMMENDED) {
      retrieveReviews();
    } else {
      setReviews(initialReviews);
    }
  }, [sortType, initialReviews]);

  const handleSortChange = (e) => {
    const { value } = e.target;
    const trackValueLabel = SORT_LABEL_BY_VALUE[value];
    setSortType(value);
    dispatch(
      ctaClickEvent('Reviews', 'Reviews Sort', {
        name: 'Profile Reviews',
        ctaStyle: 'dropdown',
        dropdownSelection: trackValueLabel,
      })
    );
  };

  if (isEmpty(reviews)) return null;

  const reviewsToRender =
    type !== reviewTypes.compact ? reviews : reviews.filter((review) => review.text.length > 0);

  return (
    <>
      {type === reviewTypes.default && (
        <>
          <Typography data-testid="reviews-section-header" variant="h2">
            {totalReviews} Reviews for {profileName}
          </Typography>
          <Dropdown
            label="Sort"
            value={sortType}
            handleChange={handleSortChange}
            options={SORT_BY_OPTIONS}
            sx={{ mt: 5, mb: 4 }}
          />
        </>
      )}
      <ReviewCards reviews={reviewsToRender} divider={type !== reviewTypes.default} type={type} />
      {type === reviewTypes.default && (
        <Box
          sx={{
            marginBottom: '43px',
            textAlign: 'center',
            '& .pagination': { display: 'flex', justifyContent: 'center' },
          }}
          data-testid="reviews-section-pagination"
        >
          <Pagination
            className="pagination"
            total={totalReviews}
            itemsPerPage={REVIEWS_PER_STANDARD_PAGE}
            page={page}
            path={`/${originalQuery.dir1}/${originalQuery.dir2}/feedback`}
          />
        </Box>
      )}
    </>
  );
};

ReviewsSection.propTypes = {
  profile: PropTypes.shape({}).isRequired,
  originalQuery: PropTypes.shape({}),
  type: PropTypes.oneOf(Object.values(reviewTypes)),
};

ReviewsSection.defaultProps = {
  type: reviewTypes.default,
  originalQuery: {},
};

export default ReviewsSection;
